import { LogOut, Settings, CreditCard, DollarSign, Mail, Gift, LayoutDashboard, CheckSquare, Image as ImageIcon, Sliders, File, HelpCircle, BarChart, FileText, Clock, Users, BookOpen, MessageSquare } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { useAdminAuth } from '../hooks/useAdminAuth'
import { useFeatureFlags } from '../hooks/useFeatureFlags'
import { isNativeApp } from '../lib/platform'
import { BILLING_ENABLED, TEAMS_ENABLED } from '../config/features'
import { APP_VERSION } from './Header'

interface AppMenuDropdownProps {
  /** メニューを閉じる（項目選択時に呼ばれる） */
  onClose: () => void
  onOpenSettings?: () => void
  onOpenContactForm: () => void
  /** 開く方向: ヘッダーからは 'down'、フッター（下部バー）からは 'up' */
  direction?: 'down' | 'up'
}

/**
 * アプリ共通のメニュードロップダウン。
 * PCはヘッダーのハンバーガーから下向きに、モバイルは下部バーから上向きに開く。
 * 親要素は position: relative であること。
 */
export default function AppMenuDropdown({ onClose, onOpenSettings, onOpenContactForm, direction = 'down' }: AppMenuDropdownProps) {
  const { signOut } = useAuth()
  const { isAdmin } = useAdminAuth()
  const { flags } = useFeatureFlags()
  const { t } = useTranslation()
  const location = useLocation()

  return (
    <div
      className={`absolute right-0 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 max-h-[70vh] overflow-y-auto ${
        direction === 'up' ? 'bottom-full mb-2' : 'mt-2'
      }`}
      data-tour="menu-dropdown"
    >
      <Link
        to="/"
        onClick={onClose}
        className={`flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50 ${
          location.pathname === '/' ? 'text-indigo-600 font-medium bg-indigo-50' : 'text-gray-700'
        }`}
        data-tour="menu-tasks"
      >
        <CheckSquare className="h-4 w-4" />
        <span>{t('header.tasks')}</span>
      </Link>
      <Link
        to="/dashboard"
        onClick={onClose}
        className={`flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50 ${
          location.pathname === '/dashboard' ? 'text-indigo-600 font-medium bg-indigo-50' : 'text-gray-700'
        }`}
        data-tour="menu-dashboard"
      >
        <LayoutDashboard className="h-4 w-4" />
        <span>{t('header.dashboard')}</span>
      </Link>
      {TEAMS_ENABLED && (
        <Link
          to="/teams"
          onClick={onClose}
          className={`flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50 ${
            location.pathname === '/teams' ? 'text-indigo-600 font-medium bg-indigo-50' : 'text-gray-700'
          }`}
          data-tour="menu-teams"
        >
          <Users className="h-4 w-4" />
          <span>{t('header.teamManagement')}</span>
        </Link>
      )}

      {/* 課金関連リンク: 無料モードでは非表示。ネイティブアプリでも非表示（Apple/Googleガイドライン対応） */}
      {BILLING_ENABLED && !isNativeApp() && (
        <>
          <div className="border-t border-gray-200 my-1"></div>

          <Link
            to="/pricing"
            onClick={onClose}
            className={`flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50 ${
              location.pathname === '/pricing' ? 'text-indigo-600 font-medium bg-indigo-50' : 'text-gray-700'
            }`}
            data-tour="menu-pricing"
          >
            <DollarSign className="h-4 w-4" />
            <span>{t('header.pricing')}</span>
          </Link>
          <Link
            to="/billing"
            onClick={onClose}
            className={`flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50 ${
              location.pathname === '/billing' ? 'text-indigo-600 font-medium bg-indigo-50' : 'text-gray-700'
            }`}
            data-tour="menu-billing"
          >
            <CreditCard className="h-4 w-4" />
            <span>{t('header.billing')}</span>
          </Link>
        </>
      )}
      {BILLING_ENABLED && flags.referral_program && (
        <Link
          to="/referral"
          onClick={onClose}
          className={`flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50 ${
            location.pathname === '/referral' ? 'text-indigo-600 font-medium bg-indigo-50' : 'text-gray-700'
          }`}
          data-tour="menu-referral"
        >
          <Gift className="h-4 w-4" />
          <span>{t('header.referral')}</span>
        </Link>
      )}
      <Link
        to="/images"
        onClick={onClose}
        className={`flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50 ${
          location.pathname === '/images' ? 'text-indigo-600 font-medium bg-indigo-50' : 'text-gray-700'
        }`}
        data-tour="menu-images"
      >
        <ImageIcon className="h-4 w-4" />
        <span>{t('header.images')}</span>
      </Link>
      <Link
        to="/files"
        onClick={onClose}
        className={`flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50 ${
          location.pathname === '/files' ? 'text-indigo-600 font-medium bg-indigo-50' : 'text-gray-700'
        }`}
        data-tour="menu-files"
      >
        <File className="h-4 w-4" />
        <span>{t('header.files')}</span>
      </Link>

      {/* Admin menu */}
      {isAdmin && (
        <>
          <div className="border-t border-gray-200 my-1"></div>
          <Link
            to="/admin/contact-management"
            onClick={onClose}
            className={`flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50 ${
              location.pathname === '/admin/contact-management' ? 'text-indigo-600 font-medium bg-indigo-50' : 'text-gray-700'
            }`}
          >
            <Mail className="h-4 w-4" />
            <span>{t('header.adminContactManagement')}</span>
          </Link>
          <Link
            to="/admin/tier-settings"
            onClick={onClose}
            className={`flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50 ${
              location.pathname === '/admin/tier-settings' ? 'text-indigo-600 font-medium bg-indigo-50' : 'text-gray-700'
            }`}
          >
            <Sliders className="h-4 w-4" />
            <span>{t('header.adminTierSettings')}</span>
          </Link>
          <Link
            to="/admin/static-content"
            onClick={onClose}
            className={`flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50 ${
              location.pathname === '/admin/static-content' ? 'text-indigo-600 font-medium bg-indigo-50' : 'text-gray-700'
            }`}
          >
            <FileText className="h-4 w-4" />
            <span>{t('header.adminStaticContent')}</span>
          </Link>
          <Link
            to="/admin/ai-usage"
            onClick={onClose}
            className={`flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50 ${
              location.pathname === '/admin/ai-usage' ? 'text-indigo-600 font-medium bg-indigo-50' : 'text-gray-700'
            }`}
          >
            <BarChart className="h-4 w-4" />
            <span>{t('header.adminAIUsage')}</span>
          </Link>
          <Link
            to="/admin/ai-settings"
            onClick={onClose}
            className={`flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50 ${
              location.pathname === '/admin/ai-settings' ? 'text-indigo-600 font-medium bg-indigo-50' : 'text-gray-700'
            }`}
          >
            <Settings className="h-4 w-4" />
            <span>{t('header.adminAISettings')}</span>
          </Link>
        </>
      )}

      <div className="border-t border-gray-200 my-1"></div>

      {onOpenSettings && (
        <button
          onClick={() => {
            onClose()
            onOpenSettings()
          }}
          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          data-tour="menu-settings"
        >
          <Settings className="h-4 w-4" />
          <span>{t('header.settings')}</span>
        </button>
      )}

      <div className="border-t border-gray-200 my-1"></div>

      <Link
        to="/terms-of-service"
        onClick={onClose}
        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
      >
        <span>{t('login.termsOfService')}</span>
      </Link>
      <Link
        to="/privacy-policy"
        onClick={onClose}
        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
      >
        <span>{t('login.privacyPolicy')}</span>
      </Link>
      {/* 特商法表記は有償販売がある場合のみ必要 */}
      {BILLING_ENABLED && (
        <Link
          to="/commercial-transactions-act"
          onClick={onClose}
          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
        >
          <span>{t('header.commercialTransactionsAct')}</span>
        </Link>
      )}
      <Link
        to="/faq"
        onClick={onClose}
        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
      >
        <span>{t('header.faq')}</span>
      </Link>
      <Link
        to="/help/videos"
        onClick={onClose}
        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
      >
        <BookOpen className="h-4 w-4" />
        <span>{t('header.helpVideos')}</span>
      </Link>
      <Link
        to="/help"
        onClick={onClose}
        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
        data-tour="menu-help"
      >
        <HelpCircle className="h-4 w-4" />
        <span>{t('header.helpManual')}</span>
      </Link>
      <button
        onClick={() => {
          onClose()
          onOpenContactForm()
        }}
        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
      >
        <Mail className="h-4 w-4" />
        <span>{t('header.newContact')}</span>
      </button>
      <Link
        to="/contact?tab=history"
        onClick={onClose}
        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
      >
        <MessageSquare className="h-4 w-4" />
        <span>{t('header.contactHistory')}</span>
      </Link>
      <Link
        to="/changelog"
        onClick={onClose}
        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
        data-tour="menu-changelog"
      >
        <Clock className="h-4 w-4" />
        <span>{t('header.changelog')}</span>
      </Link>

      <div className="border-t border-gray-200 my-1"></div>

      <button
        onClick={async () => {
          onClose()
          try {
            await signOut()
          } catch (error) {
            console.error('Logout error:', error)
            // エラーが発生してもログインページにリダイレクト
            window.location.href = '/login'
          }
        }}
        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
      >
        <LogOut className="h-4 w-4" />
        <span>{t('header.logout')}</span>
      </button>

      {/* バージョン表示（モバイルではヘッダー非表示のためここで確認できるように） */}
      <div className="border-t border-gray-200 my-1"></div>
      <div className="px-4 py-1.5 text-[10px] text-gray-400 text-center">v{APP_VERSION}</div>
    </div>
  )
}
